import { forwardRef } from 'react';
import { NavLink as RouterLink } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';

// @mui
import { Box, Stack, Typography } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';

import { SimBContainer } from '../container';
import { PageBreadcrumbs } from '.';

import { getAssetsUrl } from '../../utils/@helper/file.helper';

// ================================|| Mantis PAGE CONTAINER ||=============================== //
const MantisPageContainer = styled(Box)(
  ({ theme }) => `
    padding-bottom: ${theme.spacing(10)};
`
);
const SimMantisPageContainer = ({ children, header }) => {
  const theme = useTheme();
  return (
    <MantisPageContainer className="Sim-mantis-page-container">
      {header}
      <SimBContainer sx={{
        [theme.breakpoints.up('lg')]: {
          marginTop: theme.spacing(-24.5),
        },
        [theme.breakpoints.down('lg')]: {
          px: theme.spacing(3),
          marginTop: theme.spacing(-8),
        }
      }}>
        {children}
      </SimBContainer>
    </MantisPageContainer>
  );
};
SimMantisPageContainer.propTypes = {
  children: PropTypes.node.isRequired,
  header: PropTypes.node,
};

// =================================|| SUB PAGE HEADER ||================================= //
const MantisPageHeader = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'bgImage',
})(({ bgImage, theme }) => ({
  position: 'relative',
  backgroundPosition: 'center',
  backgroundSize: 'cover',
  backgroundRepeat: 'no-repeat',
  backgroundOrigin: 'border-box',
  backgroundColor: theme.palette.background.default,
  backgroundImage: `url('${bgImage}')`,
  paddingTop: theme.spacing(22.5),
  paddingBottom: theme.spacing(29),
  [theme.breakpoints.down('lg')]: {
    paddingTop: theme.spacing(6),
    paddingBottom: theme.spacing(6),
  },
  '&:before': {
    content: '""',
    width: '100%',
    height: '55%',
    position: 'absolute',
    left: '50%',
    top: 0,
    transform: 'translate(-50% , 0)',
    zIndex: 0,
    // backgroundImage: theme.palette.gradients.banner1,
  },
  '&:after': {
    content: '""',
    width: '100%',
    height: '100%',
    position: 'absolute',
    left: '50%',
    bottom: 0,
    transform: 'translate(-50% , 0)',
    zIndex: 0,
    backgroundImage: theme.palette.gradients.banner2,
  }
}));
const SimMantisPageHeader = ({ bgImage, title, titleAlign, subtitle }) => {
  const theme = useTheme();
  return (
    <MantisPageHeader className="Sim-mantis-page-header" bgImage={bgImage}>
      <SimBContainer sx={{ zIndex: 1 }}>
        <Stack alignItems={{ xs: 'center', md: `center`, lg: titleAlign }} justifyContent={'center'}>
          <Box display={'flex'} justifyContent={{ xs: 'center', md: `center`, lg: 'flex-end' }}>
            {
              title && (
                <Typography
                  variant={'h2'}
                  fontWeight={700}
                  textAlign={'center'}
                  color={'common.white'}
                  zIndex={1}
                  mb={!!subtitle ? 6 : 0}
                  sx={{
                    [theme.breakpoints.up('lg')]: {
                      ...(['center'].includes(titleAlign) ? theme.typography.underLine : theme.typography.leftLine)
                    },
                    [theme.breakpoints.down('lg')]: {
                      ...theme.typography.underLine
                    }
                  }}
                >
                  { title }
                </Typography>
              )
            }
          </Box>
          {
            subtitle && (
              <Box>
                {subtitle}
              </Box>
            )
          }
        </Stack>
      </SimBContainer>
    </MantisPageHeader>
  );
};
SimMantisPageHeader.propTypes = {
  title: PropTypes.string.isRequired
};


// ==================================|| Mantis PAGE BODY ||================================== //
// background-color: ${theme.palette.common.white};
// background-color: ${theme.palette.background.paper};
// background-color: ${theme.palette.background.paper};
const MantisPageBody = styled(Box)(
  ({ theme }) => `
  position: relative;
  background-color: ${theme.palette.common.white};
  box-shadow: 0px 4px 16px 0px rgba(0, 0, 0, 0.08);
`
);
const SimMantisPageBody = ({ children }) => {
  const theme = useTheme();
  return (
    <MantisPageBody
      className="Sim-mantis-page-body"
      sx={{ p: { xs: 3, sm: 3, md: 3, lg: 10 } }}
    >
      {children}
    </MantisPageBody>
  );
};
SimMantisPageBody.propTypes = {
  children: PropTypes.node.isRequired
};


// ==============================|| Mantis PAGE ||============================== //
const MantisPage = forwardRef((
  {
    title = '',
    titleAlign = 'flex-end',
    subtitle = '',
    bgImage = getAssetsUrl('images/banners/banner-base.png'),
    //
    header,
    children,
    footer,
    disableGutters = true,
    sx,
    ...other
  },
  ref
) => {
  const theme = useTheme()
  const { t } = useTranslation();

  const padding = disableGutters ? {
    padding: 0
  } : {};


  return (
    <>
      <Helmet>
        <title>{`${title || ''}`}</title>
      </Helmet>

      <SimMantisPageContainer
        header={<SimMantisPageHeader bgImage={bgImage} title={title} titleAlign={titleAlign} subtitle={subtitle} />}
      >
        <PageBreadcrumbs />
        {header}
        <SimMantisPageBody>
          {children}
        </SimMantisPageBody>
        {
          footer && (
            <Box>
              { footer }
            </Box>
          )
        }
      </SimMantisPageContainer>
    </>
  )
});

MantisPage.propTypes = {
    children: PropTypes.node.isRequired,
    title: PropTypes.string,
    meta: PropTypes.node,
};

export default MantisPage;
